import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { currentLocale } from '@shared/i18n';

type Metric = {
  key: string;
  label: string;
  value: number | null;
  hint?: string;
  tone?: 'ok' | 'warn' | 'danger' | 'grad';
  error?: string;
  to?: string; // optional admin route this card links to
};

// Maps each metric key to the admin sub-route where the user can review/fix it.
// Only listed metrics have a real destination — brand-needs-review
// intentionally has NO link because no UI exists yet for that case (admins
// need to fix via SQL or future review pages). Honest linking is better than
// misleading taps that go nowhere useful.
const METRIC_LINKS: Record<string, string> = {
  providers_total:          '/admin/bloggers',
  providers_active:         '/admin/bloggers',
  providers_with_ig:        '/admin/bloggers',
  brands_total:             '/admin/brands?filter=all',
  brands_needs_review:      '/admin/brands?filter=needs_review',
  ads_unmappable:           '/admin/brands?filter=no_subcategory',
  review_queue:             '/admin/taxonomy',
  merge_queue:              '/admin/taxonomy',
};

// Metrics with no admin page get a hint clarifying that. Avoids
// "why is this card not clickable?" confusion.
const NO_UI_HINT = 'No review UI yet — fix via SQL or open a ticket';

// Probe a table count with a safe filter; returns null if the table/col doesn't exist.
async function safeCount(
  table: string,
  build?: (q: any) => any,
): Promise<{ count: number | null; error?: string }> {
  try {
    let q: any = (supabase.from(table) as any).select('*', { count: 'exact', head: true });
    if (build) q = build(q);
    const { count, error } = await q;
    if (error) return { count: null, error: error.message };
    return { count: count ?? 0 };
  } catch (e: any) {
    return { count: null, error: e?.message ?? 'unknown' };
  }
}

export default function DataHealth() {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    const since30 = new Date(Date.now() - 30 * 86400_000).toISOString();
    const since7  = new Date(Date.now() - 7  * 86400_000).toISOString();

    // (The TV/radio taxonomy-drift probe was retired on 2026-10-27 with those channels.)
    const [probesArr] = await Promise.all([
      Promise.all([
      // Creators (instagram_providers)
      safeCount('instagram_providers'),
      safeCount('instagram_providers', (q) => q.eq('is_active', true)),
      safeCount('instagram_providers', (q) => q.not('handle', 'is', null)),
      // Brands / advertisers
      safeCount('brands'),
      safeCount('brands', (q) => q.eq('needs_review', true)),
      safeCount('advertisers'),
      safeCount('advertisers', (q) => q.is('subcategory_id', null)),
      // Mentions
      safeCount('v_all_brand_mentions'),
      safeCount('v_all_brand_mentions', (q) => q.gte('posted_at', since30)),
      safeCount('v_all_brand_mentions', (q) => q.gte('posted_at', since7)),
      // Outdoor
      safeCount('outdoor_boards'),
      safeCount('outdoor_boards', (q) => q.is('lat', null)),
      // Review queue
      safeCount('v_taxonomy_review_queue'),
      safeCount('v_taxonomy_merge_queue'),
      ]),
    ]);
    const probes = probesArr;

    const labels = [
      { key: 'providers_total',         label: t('health.providersTotal') },
      { key: 'providers_active',        label: t('health.providersActive'), tone: 'ok' as const },
      { key: 'providers_with_ig',       label: t('health.providersWithIg') },
      { key: 'brands_total',            label: t('health.brandsTotal') },
      { key: 'brands_needs_review',     label: t('health.brandsNeedsReview'), tone: 'warn' as const },
      { key: 'advertisers_total',       label: t('health.advertisersTotal') },
      { key: 'advertisers_unclassified',label: t('health.advertisersUnclass'), tone: 'warn' as const },
      { key: 'mentions_total',          label: t('health.mentionsTotal') },
      { key: 'mentions_30d',            label: t('health.mentions30d'), tone: 'grad' as const },
      { key: 'mentions_7d',             label: t('health.mentions7d') },
      { key: 'boards_total',            label: t('health.boardsTotal') },
      { key: 'boards_missing_geo',      label: t('health.boardsMissingGeo'), tone: 'warn' as const },
      { key: 'review_queue',            label: t('health.reviewQueue'), tone: 'warn' as const },
      { key: 'merge_queue',             label: t('health.mergeQueue'), tone: 'warn' as const },
    ];

    // Mark metrics that need attention but have no UI yet so users see why
    // the card isn't clickable.
    // brands_* now have a UI (/admin/brands), so removed from this set.
    const noUiKeys = new Set(['advertisers_unclassified']);

    const out: Metric[] = probes.map((p, i) => {
      const key = labels[i].key;
      const link = METRIC_LINKS[key];
      const needsAttention = !link && noUiKeys.has(key) && (p.count ?? 0) > 0;
      return {
        key,
        label: labels[i].label,
        value: p.count,
        error: p.error,
        tone: (labels[i] as any).tone,
        to: link,
        hint: needsAttention ? NO_UI_HINT : undefined,
      };
    });

    setMetrics(out);
    setRefreshedAt(new Date());
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <SectionErrorBoundary name="admin-health">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>{t('health.title')}</h2>
        <button className="admin-btn-primary" style={{ padding: '6px 12px', fontSize: 'var(--fs-small)' }} onClick={load} disabled={loading}>
          {loading ? '…' : t('health.refresh')}
        </button>
        {refreshedAt && (
          <span style={{ fontSize: 'var(--fs-small)', color: 'var(--muted)' }}>
            {t('health.lastRefresh')} {refreshedAt.toLocaleTimeString(currentLocale())}
          </span>
        )}
      </div>
      <p className="admin-page-sub">{t('health.sub')}</p>

      {loading && metrics.length === 0 && <Skeleton height={260} />}

      {metrics.length > 0 && (
        <div className="admin-kpis">
          {metrics.map((m) => {
            if (m.error) {
              return (
                <div key={m.key} className="admin-kpi err" title={m.error}>
                  <div className="lbl">{m.label}</div>
                  <div className="val">{t('health.unavailable')}</div>
                  <div className="hint" style={{ color: 'var(--danger)' }}>
                    {m.error.slice(0, 42)}{m.error.length > 42 ? '…' : ''}
                  </div>
                </div>
              );
            }
            const isPct = m.key.endsWith('_pct');
            const inner = (
              <>
                <div className="lbl">{m.label}</div>
                <div className="val">
                  {isPct ? `${(m.value ?? 0).toFixed(2)}%` : (m.value ?? 0).toLocaleString()}
                </div>
                {m.hint && (
                  <div className="hint" style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
                    {m.hint}
                  </div>
                )}
              </>
            );
            if (m.to) {
              return (
                <Link
                  key={m.key}
                  to={m.to}
                  className={`admin-kpi ${m.tone ?? ''}`}
                  title={m.hint}
                  style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <div key={m.key} className={`admin-kpi ${m.tone ?? ''}`} title={m.hint}>
                {inner}
              </div>
            );
          })}
        </div>
      )}

      <BrandAdvertiserLinkageQueue />
      <DeadCreatorsQueue />
    </SectionErrorBoundary>
  );
}

// ----- Dead / renamed creators worklist -----
// Surfaces creators the scrape/heal jobs flagged as non-alive (rename/private/gone/
// deleted). Renames auto-heal via the daily job; this is for the human decisions —
// confirm the truly gone (deactivate → drop from roster) or clear false alarms.

type DeadCreator = {
  provider_id: string; handle: string; ig_user_id: string | null; scrape_status: string;
  scrape_checked_at: string | null; is_active: boolean; deactivated_at: string | null;
  followers: number | null; engagement_rate: number | null; last_good_date: string | null;
  is_stale: boolean | null; is_private: boolean | null;
  analytics_mentions: number; posts: number; stories: number;
};

const DEAD_TONE: Record<string, { bg: string; fg: string }> = {
  deleted:         { bg: 'rgba(239,68,68,0.15)', fg: '#f87171' },
  deleted_pending: { bg: 'rgba(245,158,11,0.15)', fg: '#fbbf24' },
  gone:            { bg: 'rgba(245,158,11,0.15)', fg: '#fbbf24' },
  vacated:         { bg: 'rgba(234,179,8,0.15)', fg: '#facc15' },
  needs_merge:     { bg: 'rgba(168,85,247,0.15)', fg: '#c084fc' },
  conflict:        { bg: 'rgba(234,179,8,0.15)', fg: '#facc15' },
  private:         { bg: 'rgba(74,158,255,0.15)', fg: '#7ab8ff' },
};

function DeadCreatorsQueue() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin-dead-creators'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_dead_creators');
      if (error) throw new Error(error.message);
      return (data ?? []) as DeadCreator[];
    },
    staleTime: 30_000,
  });

  const act = async (id: string, action: 'deactivate' | 'reactivate') => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc('admin_set_creator_liveness', { p_provider_id: id, p_action: action });
      if (error) { alert(error.message); return; }
      qc.invalidateQueries({ queryKey: ['admin-dead-creators'] });
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const rows = q.data ?? [];
  return (
    <div style={{ marginTop: 24 }}>
      <h3 className="admin-page-title" style={{ fontSize: 'var(--fs-label)', margin: '0 0 4px' }}>
        {t('health.dead.title', { defaultValue: 'Dead / renamed creators' })}
      </h3>
      <p className="admin-page-sub" style={{ marginTop: 0 }}>
        {t('health.dead.sub', { defaultValue: 'Creators the scrape/heal jobs flagged as no longer live under their handle. Renames auto-heal daily; here you confirm the truly gone (deactivate → drop from the scrape roster, history kept) or clear false alarms (mark alive).' })}
      </p>

      {q.isLoading && <Skeleton height={120} />}
      {q.error && <div style={{ color: 'var(--danger)' }}>{String((q.error as Error).message ?? q.error)}</div>}
      {!q.isLoading && rows.length === 0 && (
        <div style={{
          padding: '16px 18px', borderRadius: 10,
          background: 'rgba(46,160,67,0.08)', border: '1px solid rgba(46,160,67,0.25)',
          color: '#7ee2a0', fontSize: 'var(--fs-body)',
        }}>
          ✓ {t('health.dead.empty', { defaultValue: 'No flagged creators — every scraped creator resolves live.' })}
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const tone = DEAD_TONE[r.scrape_status] ?? { bg: 'rgba(255,255,255,0.06)', fg: '#aaa' };
            const b = busy === r.provider_id;
            return (
              <div key={r.provider_id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                border: '1px solid rgba(15,45,100,0.4)', background: 'rgba(0,0,0,0.18)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <a href={`https://instagram.com/${r.handle}`} target="_blank" rel="noreferrer"
                       style={{ color: '#dde', fontWeight: 'var(--fw-semibold)', textDecoration: 'none' }}>
                      @{r.handle}
                    </a>
                    <span style={{ background: tone.bg, color: tone.fg, padding: '1px 8px', borderRadius: 20, fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)' }}>
                      {t(`health.dead.status.${r.scrape_status}`, { defaultValue: r.scrape_status })}
                    </span>
                    {!r.is_active && (
                      <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-caption)' }}>
                        · {t('health.dead.inactive', { defaultValue: 'deactivated' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 3 }}>
                    {r.followers != null
                      ? `${r.followers.toLocaleString()} ${t('health.dead.followers', { defaultValue: 'followers' })}${r.is_stale && r.last_good_date ? ` (${t('health.dead.asof', { defaultValue: 'as of' })} ${r.last_good_date})` : ''}`
                      : t('health.dead.noStats', { defaultValue: 'no stats ever' })}
                    {' · '}{r.analytics_mentions} {t('health.dead.mentions', { defaultValue: 'mentions' })}
                    {' · '}{r.posts}p / {r.stories}s
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                  <button
                    type="button" onClick={() => act(r.provider_id, 'reactivate')} disabled={b}
                    style={{ padding: '6px 10px', background: 'transparent', color: '#7ee2a0', border: '1px solid rgba(46,160,67,0.4)', borderRadius: 6, cursor: b ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)' }}>
                    {t('health.dead.markAlive', { defaultValue: 'Mark alive' })}
                  </button>
                  {r.is_active && (
                    <button
                      type="button" onClick={() => act(r.provider_id, 'deactivate')} disabled={b}
                      style={{ padding: '6px 10px', background: 'transparent', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, cursor: b ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)' }}>
                      {t('health.dead.deactivate', { defaultValue: 'Deactivate' })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----- Brand → Advertiser linkage review queue -----
// Surfaces v_brand_advertiser_candidates: orphan brands (advertiser_id IS NULL) that
// have observed-signal evidence pointing at multiple advertisers. Auto-resolution
// (single-advertiser cases) already runs on cron via backfill_brand_advertiser_from_observed.
// This UI is for the cases that need human judgement.

type Candidate = {
  brand_id: string;
  brand_name: string;
  advertiser_id: string;
  advertiser_name: string;
  weight: number;
  brand_total: number;
  share: number;
  distinct_advs: number;
  sources: string[];
};

function BrandAdvertiserLinkageQueue() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin-brand-advertiser-candidates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_brand_advertiser_candidates')
        .select('*');
      if (error) throw new Error(error.message);
      return (data ?? []) as Candidate[];
    },
    staleTime: 30_000,
  });

  // Group flat candidate rows by brand
  const grouped = useMemo(() => {
    const m = new Map<string, { brand_name: string; rows: Candidate[] }>();
    for (const r of (q.data ?? [])) {
      if (!m.has(r.brand_id)) m.set(r.brand_id, { brand_name: r.brand_name, rows: [] });
      m.get(r.brand_id)!.rows.push(r);
    }
    return Array.from(m.entries()).map(([brand_id, v]) => ({ brand_id, ...v }));
  }, [q.data]);

  const link = async (brandId: string, advertiserId: string) => {
    setBusy(brandId);
    try {
      const { error } = await supabase.rpc('admin_link_brand_advertiser', { p_brand: brandId, p_advertiser: advertiserId });
      if (error) { alert(error.message); return; }
      qc.invalidateQueries({ queryKey: ['admin-brand-advertiser-candidates'] });
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 className="admin-page-title" style={{ fontSize: 'var(--fs-label)', margin: '0 0 4px' }}>
        {t('health.linkageTitle', { defaultValue: 'Brand → advertiser review queue' })}
      </h3>
      <p className="admin-page-sub" style={{ marginTop: 0 }}>
        {t('health.linkageSub', {
          defaultValue: 'Orphan brands with multiple competing advertisers seen in IG mentions and web ads. Single-advertiser matches auto-link via cron — only ambiguous cases land here.',
        })}
      </p>

      {q.isLoading && <Skeleton height={120} />}
      {q.error && <div style={{ color: 'var(--danger)' }}>{String((q.error as Error).message ?? q.error)}</div>}
      {!q.isLoading && grouped.length === 0 && (
        <div style={{
          padding: '16px 18px', borderRadius: 10,
          background: 'rgba(46,160,67,0.08)', border: '1px solid rgba(46,160,67,0.25)',
          color: '#7ee2a0', fontSize: 'var(--fs-body)',
        }}>
          ✓ {t('health.linkageEmpty', { defaultValue: 'Queue empty — every brand with observed-signal evidence is linked.' })}
        </div>
      )}
      {grouped.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {grouped.map(({ brand_id, brand_name, rows }) => (
            <div key={brand_id} style={{
              padding: '12px 14px', borderRadius: 10,
              border: '1px solid rgba(15,45,100,0.4)', background: 'rgba(0,0,0,0.18)',
            }}>
              <div style={{ fontWeight: 'var(--fw-semibold)', marginBottom: 8 }}>{brand_name}</div>
              <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginBottom: 8 }}>
                {rows[0].brand_total} {t('health.linkageWeightUnit', { defaultValue: 'total signals across' })} {rows.length} {t('health.linkageAdvertisers', { defaultValue: 'advertisers' })}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {rows.map((r) => (
                  <div key={r.advertiser_id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 10px', borderRadius: 6,
                    background: 'rgba(255,255,255,0.03)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--fs-body)', color: '#dde' }}>{r.advertiser_name}</div>
                      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
                        {r.weight} {t('health.linkageSpots', { defaultValue: 'signals' })} ·
                        {' '}{(r.share * 100).toFixed(0)}% ·
                        {' '}{r.sources.join(' / ')}
                      </div>
                    </div>
                    <button
                      type="button" onClick={() => link(brand_id, r.advertiser_id)}
                      disabled={busy === brand_id}
                      style={{
                        padding: '6px 12px', background: '#4a9eff', color: '#fff',
                        border: 'none', borderRadius: 6,
                        cursor: busy === brand_id ? 'wait' : 'pointer',
                        fontFamily: 'inherit', fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
                      }}
                    >
                      {busy === brand_id ? '…' : t('health.linkageApply', { defaultValue: 'Link' })}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
